import React from 'react';
import { scheduleService } from '../services/scheduleService';
import ExecutionHistoryView from './schedules/ExecutionHistoryView';
import ScheduleEditor from './schedules/ScheduleEditor';
import { formatDateTime } from '../utils/dateFormatter';
import { getPollyLanguageName } from '../constants/pollyStandardVoices';
import Dismissible from './common/Dismissible';
import { usePlan } from '../hooks/usePlan';
import { openPricingModal } from '../utils/pricingModal';
import './ScheduleCard.css';

const CONVERSATIONAL_MODE_SHORT_LABELS = {
  'secondary-only': (lang) => lang ? getPollyLanguageName(lang) : 'Second language',
  'native-then-secondary': (lang) => `EN + ${lang ? getPollyLanguageName(lang) : 'second language'}`,
  'native-only': () => 'EN'
};

function ScheduleCard({ schedule, user, onUpdate, onDelete }) {
  const { status: planStatus } = usePlan();
  const [isLoading, setIsLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [showActionsMenu, setShowActionsMenu] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [showEditor, setShowEditor] = React.useState(false);

  const handlePauseResume = async () => {
    setIsLoading(true);
    try {
      const updatedSchedule = schedule.status === 'active'
        ? await scheduleService.pauseSchedule(schedule.scheduleId)
        : await scheduleService.resumeSchedule(schedule.scheduleId);
      
      onUpdate(updatedSchedule);
    } catch (error) {
      if (error.waitlisted) {
        openPricingModal();
      } else {
        alert(`Failed to ${schedule.status === 'active' ? 'pause' : 'resume'} schedule: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${schedule.scheduleName}"?`)) {
      return;
    }

    setIsLoading(true);
    try {
      await scheduleService.deleteSchedule(schedule.scheduleId);
      onDelete(schedule.scheduleId);
    } catch (error) {
      alert(`Failed to delete schedule: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = () => {
    const statusStyles = {
      active: { background: '#d4edda', color: '#155724', label: '✅ Active' },
      paused: { background: '#fff3cd', color: '#92400e', label: '⏸️ Paused' },
      deleted: { background: '#e2e8f0', color: '#4a5568', label: '🗑️ Deleted' }
    };

    const style = statusStyles[schedule.status] || statusStyles.active;

    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: '600',
        ...style
      }}>
        {style.label}
      </span>
    );
  };

  // Episode format badge (Phase 1, conversational-podcast plan). Defensive default: absent
  // episodeFormat means a legacy pre-Phase-1 schedule, which behaves as 'article'.
  const getFormatBadge = () => {
    const format = schedule.episodeFormat || 'article';

    if (format !== 'conversational') {
      return { background: '#e2e8f0', color: '#4a5568', label: '📖 Article' };
    }

    const translationLanguage = schedule.newsSources?.[0]?.translationLanguage;
    const shortLabelFn = CONVERSATIONAL_MODE_SHORT_LABELS[schedule.conversationalLanguageMode];
    const modeLabel = shortLabelFn ? shortLabelFn(translationLanguage) : '';

    return {
      background: '#e9d8fd',
      color: '#553c9a',
      label: `🗣️ Conversation${modeLabel ? ' · ' + modeLabel : ''}`
    };
  };

  return (
    <div className="schedule-card" style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: isMobile ? '1rem' : '1.5rem',
      marginBottom: '1rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s',
      position: 'relative',
      overflow: 'visible',
      ...(schedule.status === 'paused' && { opacity: 0.7 })
    }}>
      {/* Header */}
      <div className="schedule-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem', position: 'relative' }}>
        <div style={{ flex: '1 1 auto', minWidth: '0', maxWidth: isMobile ? 'calc(100% - 45px)' : '100%', paddingRight: isMobile ? '0.5rem' : '0' }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#2d3748', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', wordBreak: 'break-word' }}>
            {schedule.scheduleName}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {getStatusBadge()}
            {(() => {
              const { label, ...formatStyle } = getFormatBadge();
              return (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  ...formatStyle
                }}>
                  {label}
                </span>
              );
            })()}
            {/* Free-tier waitlist (§9, free-tier-cap-enforcement plan): the scheduler silently
                skips generation for waitlisted users ('skipped_waitlisted') while this card's
                own status/next-run-time still look normal — this badge is the only signal the
                user gets that nothing is actually being generated. */}
            {planStatus === 'waitlisted' && (
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.85rem',
                fontWeight: '600',
                background: '#fff3cd',
                color: '#92400e'
              }}>
                ⏳ Paused — you're on the waitlist
              </span>
            )}
          </div>
        </div>
        
        {/* Desktop: Regular buttons */}
        {!isMobile ? (
          <div className="schedule-card-actions" style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}>
            <button
              onClick={() => setShowHistory(true)}
              className="btn btn-secondary btn-sm"
              style={{ whiteSpace: 'nowrap' }}
            >
              📊 History
            </button>
            <button
              onClick={() => setShowEditor(true)}
              className="btn btn-secondary btn-sm"
              style={{ whiteSpace: 'nowrap' }}
            >
              ✏️ Edit
            </button>
            <button
              onClick={handlePauseResume}
              disabled={isLoading}
              className="btn btn-sm"
              style={{
                background: schedule.status === 'active' ? '#fed7d7' : '#c6f6d5',
                color: schedule.status === 'active' ? '#742a2a' : '#22543d',
                whiteSpace: 'nowrap'
              }}
            >
              {isLoading ? '...' : (schedule.status === 'active' ? '⏸️ Pause' : '▶️ Resume')}
            </button>
            <button
              onClick={handleDelete}
              disabled={isLoading}
              className="btn btn-danger btn-sm"
              style={{ whiteSpace: 'nowrap' }}
            >
              {isLoading ? '...' : '🗑️ Delete'}
            </button>
          </div>
        ) : (
          /* Mobile: Dropdown menu */
          <div style={{ position: 'absolute', top: '0', right: '0', zIndex: 100 }}>
            <button
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="schedule-card-menu-toggle"
            >
              ⋮
            </button>
            {showActionsMenu && (
              <>
                {/* Backdrop to close menu when clicking outside */}
                <div
                  onClick={() => setShowActionsMenu(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 998
                  }}
                />
                {/* Dropdown menu */}
                <div className="schedule-card-menu">
                  <button
                    onClick={() => {
                      setShowHistory(true);
                      setShowActionsMenu(false);
                    }}
                    className="schedule-card-menu__item"
                  >
                    📊 View History
                  </button>
                  <button
                    onClick={() => {
                      setShowEditor(true);
                      setShowActionsMenu(false);
                    }}
                    className="schedule-card-menu__item"
                  >
                    ✏️ Edit Schedule
                  </button>
                  <button
                    onClick={() => {
                      handlePauseResume();
                      setShowActionsMenu(false);
                    }}
                    disabled={isLoading}
                    className="schedule-card-menu__item"
                    style={{ color: schedule.status === 'active' ? '#742a2a' : '#22543d' }}
                  >
                    {isLoading ? '...' : (schedule.status === 'active' ? '⏸️ Pause' : '▶️ Resume')}
                  </button>
                  <button
                    onClick={() => {
                      setShowActionsMenu(false);
                      handleDelete();
                    }}
                    disabled={isLoading}
                    className="schedule-card-menu__item schedule-card-menu__item--danger"
                  >
                    {isLoading ? '...' : '🗑️ Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Quick Info */}
      <div className="schedule-info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', color: '#718096', marginBottom: '0.25rem' }}>🌍 Timezone</div>
          <div style={{ fontWeight: '500', color: '#2d3748', fontSize: 'clamp(0.85rem, 2vw, 1rem)', wordBreak: 'break-word' }}>{schedule.timezone}</div>
        </div>
        <div>
          <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', color: '#718096', marginBottom: '0.25rem' }}>⏰ Next Run</div>
          <div style={{ fontWeight: '500', color: '#2d3748', fontSize: 'clamp(0.85rem, 2vw, 1rem)', wordBreak: 'break-word' }}>
            {schedule.status === 'active' 
              ? scheduleService.formatNextRunTime(schedule.nextRunTime, schedule)
              : 'Paused'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', color: '#718096', marginBottom: '0.25rem' }}>📰 Source</div>
          <div style={{ fontWeight: '500', color: '#2d3748', fontSize: 'clamp(0.85rem, 2vw, 1rem)', wordBreak: 'break-word' }}>
            {schedule.newsSources && schedule.newsSources.length > 0 
              ? schedule.newsSources[0].name 
              : 'No source'}
          </div>
        </div>
      </div>

      {/* Refresh Times */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.5rem' }}>🔄 Refresh Times</div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {schedule.refreshTimes?.map((time, idx) => (
            <span
              key={idx}
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '6px',
                background: time.enabled ? '#edf2f7' : '#f7fafc',
                color: time.enabled ? '#2d3748' : '#a0aec0',
                fontSize: '0.9rem',
                border: '1px solid ' + (time.enabled ? '#cbd5e0' : '#e2e8f0'),
                textDecoration: time.enabled ? 'none' : 'line-through'
              }}
            >
              {time.localTime}
            </span>
          ))}
        </div>
      </div>

      {/* Expandable Details */}
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            padding: '0.5rem',
            border: 'none',
            background: 'transparent',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}
        >
          {expanded ? '▼ Hide Details' : '▶ Show Details'}
        </button>

        {expanded && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>
            {/* News Sources */}
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#2d3748', fontSize: '1rem' }}>📰 News Sources</h4>
              {schedule.newsSources?.map((source, idx) => (
                <div key={idx} style={{ 
                  marginBottom: '0.5rem', 
                  padding: '0.5rem', 
                  background: 'white', 
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}>
                  <div style={{ fontWeight: '500', color: '#2d3748' }}>{source.name}</div>
                  <div style={{ fontSize: '0.85rem', color: '#718096' }}>{source.url}</div>
                  <div style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: '0.25rem' }}>
                    Language: {source.sourceLanguage} | Type: {source.extractionType}
                  </div>
                </div>
              ))}
            </div>

            {/* Audio Settings */}
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#2d3748', fontSize: '1rem' }}>🎙️ Audio Settings</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#718096' }}>Speed</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>{schedule.audioSettings?.audioSpeed}x</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#718096' }}>Intro/Outro</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                    {schedule.audioSettings?.includeIntro ? '✅' : '❌'} / {schedule.audioSettings?.includeOutro ? '✅' : '❌'}
                  </div>
                </div>
              </div>
            </div>

            {/* RSS Feed URL */}
            {schedule.rssFeedUrl ? (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#e6fffa', borderRadius: '8px', border: '1px solid #81e6d9' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#234e52', fontSize: '1rem' }}>📡 RSS Feed</h4>
                <div className="rss-feed-container">
                  <input
                    type="text"
                    readOnly
                    value={schedule.rssFeedUrl}
                    className="rss-feed-url"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(schedule.rssFeedUrl);
                      alert('📋 RSS feed URL copied to clipboard!');
                    }}
                    className="copy-button"
                  >
                    📋 Copy
                  </button>
                </div>
                <Dismissible id="rss-apps-tip" title="RSS apps tip" userSub={user?.username}>
                  <p className="dismissible__reserve-right" style={{ margin: '0.5rem 0 0 0', color: '#4a5568', fontSize: '0.8rem' }}>
                    Add this URL to Apple Podcasts, Spotify, or any podcast app to receive episodes automatically.
                  </p>
                </Dismissible>
              </div>
            ) : (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#fff4e6', borderRadius: '8px', border: '1px solid #ffd89b' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#744210', fontSize: '1rem' }}>📡 RSS Feed</h4>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e0'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>⏳</span>
                  <div>
                    <p style={{ margin: 0, color: '#744210', fontWeight: '500', fontSize: '0.9rem' }}>
                      RSS Feed URL will be available after first podcast generation
                    </p>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#718096', fontSize: '0.8rem' }}>
                      Your RSS feed will automatically appear here once the first episode is generated
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Metadata */}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#718096' }}>
              <div>Created: {formatDateTime(schedule.createdAt)}</div>
              {schedule.updatedAt && schedule.updatedAt !== schedule.createdAt && (
                <div>Updated: {formatDateTime(schedule.updatedAt)}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Execution History Modal */}
      {showHistory && (
        <ExecutionHistoryView
          scheduleId={schedule.scheduleId}
          scheduleName={schedule.scheduleName}
          onClose={() => setShowHistory(false)}
        />
      )}

      {/* Schedule Editor Modal */}
      {showEditor && (
        <ScheduleEditor
          scheduleId={schedule.scheduleId}
          onComplete={(updatedSchedule) => {
            setShowEditor(false);
            onUpdate(updatedSchedule);
          }}
          onCancel={() => setShowEditor(false)}
        />
      )}
    </div>
  );
}

export default ScheduleCard;
